import React, { useMemo } from 'react';

/**
 * High-Density Vector Barcode & QR Code SVG Renderer
 * Implements ISO/IEC 15417 Code 128 barcode encoding and 2D QR matrix simulation in pure SVG.
 * Ensures strict security: values are never passed into innerHTML; all text is rendered in SVG text nodes.
 */

// Code 128 Patterns table (107 patterns for characters 0-106)
// Each pattern is an array of bar and space widths (6 elements summing to 11 modules, plus stop pattern of 13 modules)
const CODE128_PATTERNS = [
  [2,1,2,2,2,2], [2,2,2,1,2,2], [2,2,2,2,2,1], [1,2,1,2,2,3], [1,2,1,3,2,2],
  [1,3,1,2,2,2], [1,2,2,2,1,3], [1,2,2,3,1,2], [1,3,2,2,1,2], [2,2,1,2,1,3],
  [2,2,1,3,1,2], [2,3,1,2,1,2], [1,1,2,2,3,2], [1,2,2,1,3,2], [1,2,2,2,3,1],
  [1,1,3,2,2,2], [1,2,3,1,2,2], [1,2,3,2,2,1], [2,2,3,2,1,1], [2,2,1,1,3,2],
  [2,2,1,2,3,1], [2,1,3,2,1,2], [2,2,3,1,1,2], [3,1,2,1,3,1], [3,1,1,2,2,2],
  [3,2,1,1,2,2], [3,2,1,2,2,1], [3,1,2,2,1,2], [3,2,2,1,1,2], [3,2,2,2,1,1],
  [2,1,2,1,2,3], [2,1,2,3,2,1], [2,3,2,1,2,1], [1,1,1,3,2,3], [1,3,1,1,2,3],
  [1,3,1,3,2,1], [1,1,2,3,1,3], [1,3,2,1,1,3], [1,3,2,3,1,1], [2,1,1,3,1,3],
  [2,3,1,1,1,3], [2,3,1,3,1,1], [1,1,2,1,3,3], [1,1,2,3,3,1], [1,3,2,1,3,1],
  [1,1,3,1,2,3], [1,1,3,3,2,1], [1,3,3,1,2,1], [3,1,3,1,2,1], [2,1,1,3,3,1],
  [2,3,1,1,3,1], [2,1,3,1,1,3], [2,1,3,3,1,1], [2,1,3,1,3,1], [3,1,1,1,2,3],
  [3,1,1,3,2,1], [3,3,1,1,2,1], [3,1,2,1,1,3], [3,1,2,3,1,1], [3,3,2,1,1,1],
  [3,1,4,1,1,1], [2,2,1,4,1,1], [4,3,1,1,1,1], [1,1,1,2,2,4], [1,1,1,4,2,2],
  [1,2,1,1,2,4], [1,2,1,4,2,1], [1,4,1,1,2,2], [1,4,1,2,2,1], [1,1,2,2,1,4],
  [1,1,2,4,1,2], [1,2,2,1,1,4], [1,2,2,4,1,1], [1,4,2,1,1,2], [1,4,2,2,1,1],
  [2,4,1,2,1,1], [2,2,1,1,1,4], [4,1,3,1,1,1], [2,4,1,1,1,2], [1,3,4,1,1,1],
  [1,1,1,2,4,2], [1,2,1,1,4,2], [1,2,1,2,4,1], [1,1,4,2,1,2], [1,2,4,1,1,2],
  [1,2,4,2,1,1], [4,1,1,2,1,2], [4,2,1,1,1,2], [4,2,1,2,1,1], [2,1,2,1,4,1],
  [2,1,4,1,2,1], [4,1,2,1,2,1], [1,1,1,1,4,3], [1,1,1,3,4,1], [1,3,1,1,4,1],
  [1,1,4,1,1,3], [1,1,4,3,1,1], [4,1,1,1,1,3], [4,1,1,3,1,1], [1,1,3,1,4,1],
  [1,1,4,1,3,1], [3,1,1,1,4,1], [4,1,1,1,3,1], [2,1,1,4,1,2], [2,1,1,2,1,4],
  [2,1,1,2,3,2], [2,3,3,1,1,1,2] // Stop pattern (code 106)
];

const START_CODE_B = 104;
const STOP_CODE = 106;

/**
 * Encodes text into Code 128 module sequence
 */
function encodeCode128(text) {
  const cleanText = String(text || '').replace(/[^\x20-\x7E]/g, '');
  if (!cleanText) return [];

  const charCodes = [START_CODE_B];
  let checkSum = START_CODE_B;

  for (let i = 0; i < cleanText.length; i++) {
    const val = cleanText.charCodeAt(i) - 32;
    charCodes.push(val);
    checkSum += val * (i + 1);
  }

  const checkDigit = checkSum % 103;
  charCodes.push(checkDigit);
  charCodes.push(STOP_CODE);

  // Convert char codes into alternating bar and space modules
  const modules = [];
  for (let c = 0; c < charCodes.length; c++) {
    const pattern = CODE128_PATTERNS[charCodes[c]];
    if (!pattern) continue;
    let isBar = true;
    for (let p = 0; p < pattern.length; p++) {
      const width = pattern[p];
      for (let w = 0; w < width; w++) {
        modules.push(isBar);
      }
      isBar = !isBar;
    }
  }

  return modules;
}

/**
 * Generates pseudo QR code matrix for 2D barcode preview
 */
function generateQrMatrix(text) {
  const size = 21; // Version 1 QR matrix (21x21)
  const matrix = Array.from({ length: size }, () => Array(size).fill(false));

  // Finder pattern helper
  const drawFinder = (startX, startY) => {
    for (let r = 0; r < 7; r++) {
      for (let c = 0; c < 7; c++) {
        if (
          r === 0 || r === 6 || c === 0 || c === 6 ||
          (r >= 2 && r <= 4 && c >= 2 && c <= 4)
        ) {
          matrix[startY + r][startX + c] = true;
        }
      }
    }
  };

  // 3 Finder patterns
  drawFinder(0, 0);
  drawFinder(size - 7, 0);
  drawFinder(0, size - 7);

  // Timing patterns
  for (let i = 8; i < size - 8; i++) {
    matrix[6][i] = i % 2 === 0;
    matrix[i][6] = i % 2 === 0;
  }

  // Deterministic hash based data fill
  let hash = 0;
  const str = String(text || '10000001');
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }

  for (let r = 8; r < size; r++) {
    for (let c = 8; c < size; c++) {
      const bit = ((hash ^ (r * 31 + c * 17)) & 1) === 1;
      matrix[r][c] = bit;
    }
  }

  return matrix;
}

export const BarcodeSvgRenderer = ({
  value = '10000001',
  symbology = 'CODE128',
  showText = true,
  height = 50,
  quietZone = 10,
  barColor = '#000000',
  textColor = '#0f172a',
  fontSize = 11,
  className = '',
  style = {}
}) => {
  // Ensure safe string conversion
  const safeValue = useMemo(() => String(value || '').trim(), [value]);
  const is2D = symbology === 'QR_CODE' || symbology === 'DATAMATRIX';

  // 1D Code 128 Modules
  const modules = useMemo(() => {
    if (is2D) return [];
    return encodeCode128(safeValue);
  }, [safeValue, is2D]);

  // 2D QR Matrix
  const qrMatrix = useMemo(() => {
    if (!is2D) return [];
    return generateQrMatrix(safeValue);
  }, [safeValue, is2D]);

  if (is2D) {
    const matrixSize = qrMatrix.length;
    const cellSize = 3;
    const qrWidth = matrixSize * cellSize + quietZone * 2;
    const qrHeight = matrixSize * cellSize + quietZone * 2 + (showText ? 14 : 0);

    return (
      <svg
        className={`barcode-svg-render ${className}`}
        viewBox={`0 0 ${qrWidth} ${qrHeight}`}
        style={{ width: '100%', height: '100%', display: 'block', ...style }}
        xmlns="http://www.w3.org/2000/svg"
        shapeRendering="crispEdges"
        aria-label={`Barcode: ${safeValue}`}
      >
        <rect width={qrWidth} height={qrHeight} fill="#ffffff" />
        <g transform={`translate(${quietZone}, ${quietZone})`}>
          {qrMatrix.map((row, r) =>
            row.map((col, c) =>
              col ? (
                <rect
                  key={`${r}-${c}`}
                  x={c * cellSize}
                  y={r * cellSize}
                  width={cellSize}
                  height={cellSize}
                  fill={barColor}
                />
              ) : null
            )
          )}
        </g>
        {showText && (
          <text
            x={qrWidth / 2}
            y={qrHeight - 3}
            textAnchor="middle"
            fill={textColor}
            fontSize={fontSize}
            fontFamily="monospace"
            fontWeight="bold"
          >
            {safeValue}
          </text>
        )}
      </svg>
    );
  }

  // 1D Linear Barcode Rendering
  const moduleWidth = 1.6;
  const barcodeBarsWidth = modules.length * moduleWidth;
  const totalWidth = barcodeBarsWidth + quietZone * 2;
  const textSpace = showText ? 14 : 0;
  const barHeight = Math.max(20, height - textSpace);
  const totalHeight = barHeight + textSpace;

  return (
    <svg
      className={`barcode-svg-render ${className}`}
      viewBox={`0 0 ${totalWidth} ${totalHeight}`}
      style={{ width: '100%', height: '100%', display: 'block', ...style }}
      xmlns="http://www.w3.org/2000/svg"
      shapeRendering="crispEdges"
      aria-label={`Barcode: ${safeValue}`}
    >
      <rect width={totalWidth} height={totalHeight} fill="#ffffff" />
      <g transform={`translate(${quietZone}, 0)`}>
        {modules.map((isBar, idx) =>
          isBar ? (
            <rect
              key={idx}
              x={idx * moduleWidth}
              y={0}
              width={moduleWidth}
              height={barHeight}
              fill={barColor}
            />
          ) : null
        )}
      </g>
      {showText && (
        <text
          x={totalWidth / 2}
          y={totalHeight - 2}
          textAnchor="middle"
          fill={textColor}
          fontSize={fontSize}
          fontFamily="monospace"
          fontWeight="bold"
          letterSpacing="0.8px"
        >
          {safeValue}
        </text>
      )}
    </svg>
  );
};

export default BarcodeSvgRenderer;
